<template>
    <div class="events-semanal-view">
        <el-empty
            v-if="weeks.length === 0"
            description="Nenhum evento neste mês"
        />

        <div v-else class="d-flex flex-column" :style="{ gap: '16px' }">
            <div   
                v-for="week in weeks"
                :key="week.number"
                class="border rounded p-1"
            >
                <div class="p-1 border rounded mb-2" :style="{ backgroundColor: '#f0f9ebCC' }">
                     <!-- Header da semana -->
                    <div class="d-flex align-items-center justify-content-between flex-wrap" :style="{ gap: '12px' }">
                        <div class="pl-2 d-flex align-items-center" :style="{ gap: '10px' }">
                            <i class="el-icon-date" :style="{ color: '#67C23A' }"></i>
                            <h6 class="mb-0 font-weight-bolder">Semana {{ week.number }}</h6>
                            <el-tag size="mini" type="success" effect="plain" class="rounded-pill">
                                {{ week.events.length }} evento{{ week.events.length === 1 ? '' : 's' }}
                            </el-tag>
                        </div>

                        <el-button
                            v-if="!isExport && (isSelectionModeActive(week.number) || getSelectableEvents(week).length > 0)"
                            size="small"
                            :type="isSelectionModeActive(week.number) ? 'danger' : 'primary'"
                            plain
                            round
                            :disabled="isWeekButtonDisabled(week.number)"
                            @click.native.prevent="toggleWeekSelection(week.number)"
                        >
                            <i :class="isSelectionModeActive(week.number) ? 'el-icon-close' : 'el-icon-check'"></i>
                            {{ isSelectionModeActive(week.number) ? 'Cancelar seleção' : 'Selecionar eventos' }}
                        </el-button>
                    </div>
                </div>

                <!-- Painel de ações da semana em modo de seleção -->
                <div
                    v-if="isSelectionModeActive(week.number)"
                    class="week-actions-panel mb-3"
                >
                    <div class="d-flex align-items-center justify-content-between flex-wrap" :style="{ gap: '12px' }">
                        <span :style="{ fontSize: '13px' }">
                            <b>{{ selectedEventIds.length }}</b>
                            evento{{ selectedEventIds.length === 1 ? '' : 's' }} selecionado{{ selectedEventIds.length === 1 ? '' : 's' }}
                        </span>

                        <div class="d-flex align-items-center flex-wrap" :style="{ gap: '8px' }">
                            <el-button
                                size="small"
                                type="info"
                                plain
                                :disabled="!canReproveSelected || isProcessingAction"
                                @click.native.prevent="handleDisapproveSelectedEvents()"
                            >
                                <i v-if="isProcessingAction" class="el-icon-loading"></i>
                                <i v-else class="el-icon-close"></i>
                                Reprovar
                            </el-button>

                            <el-button
                                size="small"
                                type="success"
                                plain
                                :disabled="!canApproveSelected || isProcessingAction"
                                @click.native.prevent="handleApproveSelectedEvents()"
                            >
                                <i v-if="isProcessingAction" class="el-icon-loading"></i>
                                <i v-else class="el-icon-check"></i>
                                Aprovar
                            </el-button>

                            <el-button
                                size="small"
                                type="warning"
                                plain
                                :disabled="!canRevokeSelected || isProcessingAction"
                                @click.native.prevent="handleRevokeApprovedSelectedEvents()"
                            >
                                <i v-if="isProcessingAction" class="el-icon-loading"></i>
                                <i v-else class="el-icon-refresh-left"></i>
                                Revogar aprovação
                            </el-button>
                        </div>
                    </div>
                </div>

                <!-- Lista de eventos da semana -->
                <div class="d-flex flex-column" :style="{ gap: '8px' }">
                    <!-- content -->   
                    <el-row>
                        <el-col>
                            <el-table
                                class="rounded"
                                empty-text="Nenhum evento encontrado"
                                :data="week.events"
                                align="center"
                                border
                                row-key="id"
                                :row-class-name="getRowClassName"
                            >
                                <el-table-column width="40" align="center">
                                    <template slot="header" slot-scope="scope">
                                        <el-checkbox
                                            v-if="isSelectionModeActive(week.number)"
                                            :value="isWeekFullySelected(week)"
                                            :indeterminate="isWeekPartiallySelected(week)"
                                            :disabled="getSelectableEvents(week).length === 0"
                                            @change="toggleWeekHeaderCheckbox(week)"
                                        />
                                    </template>

                                    <template
                                        v-if="isSelectionModeActive(week.number)"
                                        slot-scope="scope"
                                    >
                                        <el-tooltip
                                            v-if="!isEventCheckable(scope.row)"
                                            :content="getNotCheckableReason(scope.row)"
                                            placement="right"
                                        >
                                            <span>
                                                <el-checkbox disabled :value="false" />
                                            </span>
                                        </el-tooltip>

                                        <el-checkbox
                                            v-else
                                            :value="isEventSelected(scope.row.id)"
                                            @change="toggleEventSelection(scope.row)"
                                        />
                                    </template>
                                </el-table-column>

                                <!-- Data -->
                                <el-table-column label="Data do evento" width="130" align="center">
                                    <template slot-scope="scope">
                                        <div class="d-flex align-items-center justify-content-center">
                                            <i class="el-icon-time"></i>
                                            <span class="ml-2">{{ formatDate(scope.row.data) }}</span>
                                        </div>
                                    </template>
                                </el-table-column>

                                <!-- Status -->
                                <el-table-column 
                                    label="Status" 
                                    prop="status" 
                                    align="left"
                                    width="130"
                                >
                                    <div slot-scope="scope">
                                        <el-tag
                                            :type="getEventColorByStatus(scope.row.status)"
                                            class="rounded-pill text-center"
                                            size="small"
                                            :style="{ minWidth: '100px' }"
                                        >
                                            {{ scope.row.status }}
                                        </el-tag>
                                    </div>
                                </el-table-column>

                                <!-- Titulo -->
                                <el-table-column 
                                    label="Titulo" 
                                    prop="titulo" 
                                    align="left"
                                    min-width="200"
                                >
                                    <div slot-scope="scope">{{ scope.row.titulo }}</div>
                                </el-table-column>

                                <!-- Despesas -->
                                <el-table-column 
                                    label="Despesas" 
                                    align="left"
                                    width="140"
                                >   
                                    <div slot-scope="scope">
                                        <div class="d-flex align-items-center" :style="{ gap: '4px', fontSize: '16px' }">
                                            <el-tooltip
                                                v-if="scope.row.viagem"
                                                content="Viagem"
                                                placement="left"
                                            >
                                                <span class="border rounded p-1">
                                                    <i class="el-icon-map-location"/>
                                                </span>
                                            </el-tooltip>

                                            <el-tooltip
                                                v-if="scope.row.hospedagem"
                                                content="Hospedagem"
                                                placement="left"
                                            >
                                                <span class="border rounded p-1">
                                                    <i class="el-icon-school"/>
                                                </span>
                                            </el-tooltip>

                                            <el-tooltip
                                                v-if="scope.row.almoco"
                                                content="Almoço"
                                                placement="left"
                                            >
                                                <span class="border rounded p-1">
                                                    <i class="el-icon-food"/>
                                                </span>
                                            </el-tooltip>

                                            <el-tooltip
                                                v-if="scope.row.jantar"
                                                content="Jantar"
                                                placement="left"
                                            >
                                                <span class="border rounded p-1">
                                                    <i class="el-icon-dish"/>
                                                </span>
                                            </el-tooltip>
                                        </div>
                                    </div>
                                </el-table-column>

                                <!-- Analisado por -->
                                <el-table-column label="Analisado por" width="230" align="left">
                                    <template slot-scope="scope">
                                        <div 
                                            v-if="scope.row.analisado_por" 
                                            class="d-flex align-items-center"
                                            :style="{ gap: '8px' }"
                                        >
                                            <el-avatar size="small">
                                                {{ scope.row.analisado_por.name.charAt(0) }}
                                            </el-avatar>

                                            <div 
                                                class="d-flex flex-column" 
                                                :style="{ minWidth: 0, flex: 1 }"
                                            >
                                                <span 
                                                    class="text-truncate text-capitalize"
                                                    :style="{ fontSize: '12px' }"
                                                >
                                                    {{ scope.row.analisado_por.name.toLowerCase() }}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <el-tag 
                                            v-else
                                            type="info" 
                                            size="small"
                                        >
                                            Não analisado
                                        </el-tag>
                                    </template>
                                </el-table-column>

                                <!-- Acoes -->
                                <el-table-column 
                                    v-if="!isExport"
                                    fixed="right"
                                    :width="75"
                                    align="right"
                                >
                                    <div slot="header" style="width: 100%;">
                                        <i class="el-icon-menu" />
                                    </div>
                                    <template slot-scope="scope">
                                        <div class="d-flex justify-content-between">
                                            <div :style="{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', gap: '4px', maxWidth: '300px' }">
                                                <el-button
                                                    size="mini" 
                                                    type="primary" 
                                                    plain
                                                    :style="{ width: '100%' }"
                                                    @click.native.prevent="dispatchEvent(scope.row)"
                                                >
                                                    <i class="el-icon-right"></i>
                                                    <i class="el-icon-date"></i>
                                                </el-button>
                                            </div>
                                        </div>
                                    </template>
                                </el-table-column>
                            </el-table>
                        </el-col>
                    </el-row>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import {
        Card,
        Button,
        Checkbox,
        Tag,
        Empty,
        Notification,
        Table,
        TableColumn,
        Row,
        Col,
        Avatar,
        Tooltip,
    } from "element-ui";

    // Status que podem receber ação em lote. Qualquer outro status
    // (REPROVADO, CUMPRIDO, NAO CUMPRIU, etc.) não pode ser selecionado
    // nem enviado nas ações em massa.
    const SELECTABLE_STATUSES = ['PENDENTE', 'APROVADO'];

    // Utils
    import { formatDate } from "@/util/formatters";

    // Api
    import { AgendaApi } from "@/api/AgendaApi";

    const agendaApi = new AgendaApi();

    export default {
        name: "EventsSemanalView",
        props: {
            // Mesma coleção de eventos fornecida ao FullCalendar (registros
            // crus da agenda: { id, titulo, status, data, ... })
            events: {
                type: Array,
                default: () => [],
            },
            // Data de referência do mês exibido no calendário. Se não for
            // fornecida, assume o mês/ano atual.
            referenceDate: {
                type: [String, Date],
                default: null,
            },
            isExport: {
                type: Boolean,
                default: false,
            }
        },
        data() {
            return {
                // Número da semana atualmente em modo de seleção (somente
                // uma por vez). null = nenhuma semana em seleção.
                activeWeekIndex: null,
                // Ids (reativos) dos eventos selecionados na semana ativa
                selectedEventIds: [],
                isProcessingAction: false,
            }
        },
        methods: {
            /** Converte a data do evento evitando shift de dia por timezone */
            parseEventDate(event) {
                if (!event || !event.data) {
                    return null;
                }

                const rawDate = String(event.data).split('T')[0];

                return new Date(`${rawDate}T12:00:00`);
            },
            getEventColorByStatus(status) {
                switch (status) {
                    case 'APROVADO':
                        return 'success'
                    case 'PENDENTE':
                        return 'warning'
                    case 'CUMPRIDO':
                        return ''
                    case 'NAO CUMPRIU':
                        return 'danger'
                    case 'REPROVADO':
                        return 'info'
                    default:
                        return 'info'
                }
            },
            /** Alterna o modo de seleção de uma semana */
            toggleWeekSelection(weekNumber) {
                if (this.activeWeekIndex === weekNumber) {
                    this.activeWeekIndex = null;
                } else {
                    this.activeWeekIndex = weekNumber;
                }

                this.selectedEventIds = [];
            },
            // Enquanto uma semana estiver em modo de seleção, os botões
            // "Selecionar eventos" das demais semanas ficam desabilitados
            isWeekButtonDisabled(weekNumber) {
                return this.activeWeekIndex !== null && this.activeWeekIndex !== weekNumber;
            },
            isSelectionModeActive(weekNumber) {
                return this.activeWeekIndex === weekNumber;
            },
            /**
             * Mesma regra de autorização do "validateApprove" do
             * EventShowModal.vue, porém avaliada por evento (aqui podemos
             * ter eventos de vários usuários numa mesma semana):
             * - adm/coord_comercial podem agir sobre qualquer evento
             *   (o próprio ou de terceiros);
             * - diretor_nacional/gerente_nacional/gerente_regional só podem
             *   agir sobre eventos de OUTROS usuários (não o próprio);
             * - qualquer outro caso (inclusive um usuário comum vendo o
             *   próprio evento) não tem permissão.
             */
            isUserAuthorizedForEvent(event) {
                if (!event) {
                    return false;
                }

                if (this.$store.getters['user/isAnyRole'](['adm', 'coord_comercial'])) {
                    return true;
                }

                if (
                    this.getUserId !== event.user_id
                    && this.$store.getters['user/isAnyRole'](['diretor_nacional', 'gerente_nacional', 'gerente_regional'])
                ) {
                    return true;
                }

                return false;
            },
            /**
             * Somente eventos PENDENTE ou APROVADO podem ser marcados/enviados
             * em ações de lote (um evento REPROVADO, CUMPRIDO, etc. já teve
             * seu ciclo de aprovação encerrado), e somente se o usuário logado
             * tiver permissão sobre aquele evento específico.
             */
            isEventCheckable(event) {
                return !!event
                    && SELECTABLE_STATUSES.includes(event.status)
                    && this.isUserAuthorizedForEvent(event);
            },
            getSelectableEvents(week) {
                return week.events.filter(this.isEventCheckable);
            },
            getRowClassName({ row }) {
                return this.isEventCheckable(row) ? '' : 'row-not-selectable';
            },
            getNotCheckableReason(event) {
                if (!SELECTABLE_STATUSES.includes(event.status)) {
                    return 'Somente eventos Pendentes ou Aprovados podem ser selecionados';
                }

                if (!this.isUserAuthorizedForEvent(event)) {
                    return 'Você não tem permissão para agir sobre este evento';
                }

                return '';
            },
            toggleEventSelection(event) {
                // Guarda extra: mesmo que o checkbox não devesse estar
                // habilitado para esse status, nunca permite selecionar
                if (!this.isEventCheckable(event)) {
                    return;
                }

                const index = this.selectedEventIds.indexOf(event.id);

                if (index === -1) {
                    this.selectedEventIds.push(event.id);
                } else {
                    this.selectedEventIds.splice(index, 1);
                }
            },
            isEventSelected(eventId) {
                return this.selectedEventIds.includes(eventId);
            },
            /** Checkbox do header: seleciona/desmarca todos os eventos elegíveis da semana */
            toggleWeekHeaderCheckbox(week) {
                const selectableEvents = this.getSelectableEvents(week);

                if (this.isWeekFullySelected(week)) {
                    this.selectedEventIds = [];
                } else {
                    this.selectedEventIds = selectableEvents.map(event => event.id);
                }
            },
            isWeekFullySelected(week) {
                const selectableEvents = this.getSelectableEvents(week);

                return selectableEvents.length > 0
                    && selectableEvents.every(event => this.selectedEventIds.includes(event.id));
            },
            isWeekPartiallySelected(week) {
                return !this.isWeekFullySelected(week)
                    && this.getSelectableEvents(week).some(event => this.selectedEventIds.includes(event.id));
            },
            /** Atualiza em lote o status de todos os eventos selecionados */
            async bulkUpdateEventsStatus(status) {
                if (this.selectedEventIds.length === 0) {
                    return;
                }

                this.isProcessingAction = true;

                try {
                    await Promise.all(
                        this.selectedEventIds.map(eventId => agendaApi.update({
                            analisado_por: this.getUserId ? this.getUserId.toString() : null,
                            status,
                        }, eventId))
                    );

                    Notification.success({
                        title: 'Sucesso',
                        message: this.getBulkSuccessMessage(status),
                        position: 'top-left'
                    });

                    this.activeWeekIndex = null;
                    this.selectedEventIds = [];
                } catch (error) {
                    Notification.error({
                        title: 'Erro',
                        message: 'Não foi possível atualizar os eventos selecionados!',
                        position: 'top-left'
                    });
                } finally {
                    this.isProcessingAction = false;
                    this.$emit('dispatch');
                }
            },
            getBulkSuccessMessage(status) {
                switch (status) {
                    case 'APROVADO':
                        return 'Evento(s) aprovado(s)!'
                    case 'REPROVADO':
                        return 'Evento(s) reprovado(s)!'
                    case 'PENDENTE':
                        return 'Aprovação do(s) evento(s) revogada!'
                    default:
                        return 'Evento(s) atualizado(s)!'
                }
            },
            handleApproveSelectedEvents() {
                this.$confirm(
                    `Deseja aprovar o(s) ${this.selectedEventIds.length} evento(s) selecionado(s)?`,
                    'Aprovação de eventos',
                    {
                        confirmButtonText: 'Aprovar eventos',
                        cancelButtonText: 'Cancelar',
                    }
                ).then(async () => {
                    await this.bulkUpdateEventsStatus('APROVADO');
                }).catch(() => {});
            },
            handleDisapproveSelectedEvents() {
                this.$confirm(
                    `Deseja reprovar o(s) ${this.selectedEventIds.length} evento(s) selecionado(s)?`,
                    'Reprovação de eventos',
                    {
                        confirmButtonText: 'Reprovar eventos',
                        cancelButtonText: 'Cancelar',
                    }
                ).then(async () => {
                    await this.bulkUpdateEventsStatus('REPROVADO');
                }).catch(() => {});
            },
            handleRevokeApprovedSelectedEvents() {
                this.$confirm(
                    `Deseja revogar a aprovação do(s) ${this.selectedEventIds.length} evento(s) selecionado(s)?`,
                    'Revogação de eventos',
                    {
                        confirmButtonText: 'Revogar eventos',
                        cancelButtonText: 'Cancelar',
                    }
                ).then(async () => {
                    await this.bulkUpdateEventsStatus('PENDENTE');
                }).catch(() => {});
            },
            dispatchEvent(payload) {
                this.$emit('handleEvent', {
                    event: {
                        id: payload.id,
                        start: payload.data
                    }
                })
            },
            formatDate,
        },
        computed: {
            getUserId() {
                return this.$store?.getters['user/getUserId'];
            },
            /** Objetos completos dos eventos atualmente selecionados */
            selectedEventsObjects() {
                return (this.events || []).filter(
                    event => this.selectedEventIds.includes(event.id)
                );
            },
            // Aprovar/Reprovar só fazem sentido para eventos ainda PENDENTES.
            // Exige que TODOS os selecionados estejam nesse status, evitando
            // que uma seleção mista altere eventos que não deveriam mudar.
            canApproveSelected() {
                return this.selectedEventsObjects.length > 0
                    && this.selectedEventsObjects.every(event => event.status === 'PENDENTE');
            },
            canReproveSelected() {
                return this.selectedEventsObjects.length > 0
                    && this.selectedEventsObjects.every(event => event.status === 'PENDENTE');
            },
            // Revogar aprovação só é válido para eventos já APROVADOS.
            canRevokeSelected() {
                return this.selectedEventsObjects.length > 0
                    && this.selectedEventsObjects.every(event => event.status === 'APROVADO');
            },
            refDate() {
                return this.referenceDate ? new Date(this.referenceDate) : new Date();
            },
            // Unifica os eventos do mês de referência em semanas, no mesmo
            // formato de agrupamento usado pela grade mensal do FullCalendar
            // (semanas de domingo a sábado)
            weeks() {
                const year = this.refDate.getFullYear();
                const month = this.refDate.getMonth();

                const monthEvents = (this.events || []).filter(event => {
                    const eventDate = this.parseEventDate(event);

                    return eventDate
                        && eventDate.getFullYear() === year
                        && eventDate.getMonth() === month;
                });

                const firstWeekday = new Date(year, month, 1).getDay();

                const buckets = {};

                monthEvents.forEach(event => {
                    const eventDate = this.parseEventDate(event);
                    const dayOfMonth = eventDate.getDate();
                    const weekNumber = Math.floor((dayOfMonth - 1 + firstWeekday) / 7) + 1;

                    if (!buckets[weekNumber]) {
                        buckets[weekNumber] = [];
                    }

                    buckets[weekNumber].push(event);
                });

                return Object.keys(buckets)
                    .map(Number)
                    .sort((a, b) => a - b)
                    .map(weekNumber => ({
                        number: weekNumber,
                        events: buckets[weekNumber].sort(
                            (a, b) => this.parseEventDate(a) - this.parseEventDate(b)
                        ),
                    }));
            },
        },
        watch: {
            // Evita manter seleção/ids de uma listagem que não existe mais
            // (ex: após refetch do componente pai)
            events() {
                this.activeWeekIndex = null;
                this.selectedEventIds = [];
            },
            refDate() {
                this.activeWeekIndex = null;
                this.selectedEventIds = [];
            },
        },
        components: {
            [Card.name]: Card,
            [Button.name]: Button,
            [Checkbox.name]: Checkbox,
            [Tag.name]: Tag,
            [Empty.name]: Empty,
            [Notification.name]: Notification,
            [Table.name]: Table,
            [TableColumn.name]: TableColumn,
            [Row.name]: Row,
            [Col.name]: Col,
            [Avatar.name]: Avatar,
            [Tooltip.name]: Tooltip,
        },
    }
</script>
<style lang="scss">
    .events-semanal-view {
        .week-card {
            border-radius: 15px;
            
            .el-card__body {
                padding: 12px;
            }
        }

        label {
            line-height: 1;
        }

        .week-actions-panel {
            background-color: #fff;
            border: 1px dashed #dcdfe6;
            border-radius: 12px;
            padding: 10px 14px;
            position: fixed;
            bottom: 12px;
            width: calc(100% - 20%);
            left: 50%;
            z-index: 999;
            transform: translateX(-50%);
            box-shadow: 0px 0px 1px #000C;
        }

        .week-event-row {
            padding: 2px 8px;
            border-radius: 8px;

            &.is-selected {
                background-color: #ecf5ff;
            }

            &:hover {
                background-color: #f5f7fa;
            }
        }
    }
</style>


// WEBPACK FOOTER //
// resources/assets/js/components/agenda/EventsSemanalView.vue